import type { SupabaseClient } from '@supabase/supabase-js';

export const passwordCallbackPath = '/api/auth/callback';
export const passwordSigninPath = `${passwordCallbackPath}?view=signin`;
export const passwordRecoveryRedirect = (origin: string) =>
  `${origin}${passwordCallbackPath}?auth=recovery`;
const marker = 'sqi-password-setup-pending';
const identity = 'sqi-password-setup-user';
const operation = 'sqi-password-setup-operation';
export const passwordSetupOperation = (storage: Storage) => storage.getItem(operation);
export function capturePasswordCallback(url: URL, storage: Storage, clearUrl: () => void) {
  const hash = new URLSearchParams(url.hash.slice(1));
  const type = url.searchParams.get('type') ?? hash.get('type') ?? url.searchParams.get('auth');
  const hasCredentials =
    ['token_hash', 'code', 'error', 'error_description'].some((k) => url.searchParams.has(k)) ||
    hash.has('access_token') ||
    hash.has('error');
  const pending =
    (url.pathname === passwordCallbackPath && url.searchParams.get('view') !== 'signin') ||
    type === 'invite' ||
    type === 'recovery' ||
    hasCredentials ||
    storage.getItem(marker) === 'true';
  if (pending) storage.setItem(marker, 'true');
  const operationId = url.searchParams.get('operation');
  const validOperation = Boolean(
    operationId &&
    /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(operationId),
  );
  if (
    operationId &&
    /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(operationId)
  )
    storage.setItem(operation, operationId);
  if (pending || hasCredentials) clearUrl();
  return {
    pending,
    type:
      type === 'invite' || type === 'recovery' || (type === 'magiclink' && validOperation)
        ? type
        : null,
    tokenHash: url.searchParams.get('token_hash'),
    accessToken: hash.get('access_token'),
    refreshToken: hash.get('refresh_token'),
    failed:
      (type === 'magiclink' && !validOperation) ||
      url.searchParams.has('error') ||
      hash.has('error') ||
      url.searchParams.has('code'),
  } as const;
}
export async function verifyPasswordCallback(
  auth: SupabaseClient['auth'],
  callback: ReturnType<typeof capturePasswordCallback>,
  storage: Storage,
) {
  if (!callback.pending) return;
  if (callback.failed) throw Error('setup_link_invalid');
  let expected = storage.getItem(identity);
  if (callback.tokenHash && callback.type) {
    const { data, error } = await auth.verifyOtp({
      token_hash: callback.tokenHash,
      type: callback.type,
    });
    if (error || !data.user) throw Error('setup_link_invalid');
    expected = data.user.id;
  } else if (callback.accessToken && callback.refreshToken && callback.type) {
    const { data, error } = await auth.setSession({
      access_token: callback.accessToken,
      refresh_token: callback.refreshToken,
    });
    if (error || !data.user) throw Error('setup_link_invalid');
    expected = data.user.id;
  } else if (callback.tokenHash || callback.accessToken) throw Error('setup_link_invalid');
  const { data, error } = await auth.getUser();
  if (error || !data.user || !expected || data.user.id !== expected)
    throw Error('setup_link_invalid');
  storage.setItem(identity, expected);
}
export async function finishPasswordSetup(
  auth: SupabaseClient['auth'],
  storage: Storage,
  password: string,
  confirmation: string,
  acceptScope?: (password: string) => Promise<unknown>,
) {
  if (password.length < 12 || password !== confirmation) throw Error('password_invalid');
  const { data, error } = await auth.getUser();
  if (
    error ||
    !data.user ||
    storage.getItem(marker) !== 'true' ||
    data.user.id !== storage.getItem(identity)
  )
    throw Error('setup_link_invalid');
  if (storage.getItem(operation) && !acceptScope) throw Error('scope_consent_required');
  if (storage.getItem(operation) && acceptScope) await acceptScope(password);
  else {
    const result = await auth.updateUser({ password });
    if (result.error) throw Error('password_update_failed');
  }
  const signedOut = await auth.signOut();
  if (signedOut.error) throw Error('setup_logout_failed');
  storage.removeItem(marker);
  storage.removeItem(identity);
  storage.removeItem(operation);
}
export async function cancelPasswordSetup(auth: SupabaseClient['auth'], storage: Storage) {
  const result = await auth.signOut();
  if (result.error) throw Error('setup_logout_failed');
  storage.removeItem(marker);
  storage.removeItem(identity);
  storage.removeItem(operation);
}
